import { Box, IconButton, Tooltip } from "@mui/material";
import TextFieldsIcon from "@mui/icons-material/TextFields";
import WidgetsIcon from "@mui/icons-material/Widgets";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import MoveUpSharpIcon from "@mui/icons-material/MoveUpSharp";
import MoveDownSharpIcon from "@mui/icons-material/MoveDownSharp";
import { COMPONENT_TYPES } from "../utils/constants";
import AddUp from "../../../../../src/assets/vector-for-add-up.svg";
import AddBelow from "../../../../../src/assets/vector-for-add-below.svg";
import EditIcon from "@mui/icons-material/Edit";

const Sidebar = ({
  components,
  selectedId,
  onAddComponent,
  onDeleteComponent,
  onMoveComponent,
  onOpenWidgetPopup,
  onInsertAbove,
  onInsertBelow,
  onCloseSidebar,
  onEditWidget,
  setIsTextEditing,
  activeInsertPosition,
  setActiveInsertPosition,
  isTextEditing,
}: any) => {
  const selectedComponent = components.find((c: any) => c.id === selectedId);

  const createComponent = (type: any, parentId = null) => {
    const id = Date.now().toString();
    if (type === COMPONENT_TYPES.TEXT) {
      const component = {
        id,
        type,
        parentId,
        props: {
          text: "Edit this text",
          fontSize: 16,
          textAlign: "left",
          color: "#000000",
        },
      };
      onAddComponent(component);
    }
  };

  const handleInsertClick = (position: "above" | "below") => {
    setActiveInsertPosition(position);
  };

  const handleInsert = (type: string) => {
    if (activeInsertPosition === "above") {
      onInsertAbove(type);
    } else if (activeInsertPosition === "below") {
      onInsertBelow(type);
    }
    setActiveInsertPosition(null);
  };

  const handleCancelInsert = () => {
    setActiveInsertPosition(null);
  };

  return (
    <Box
      sx={{
        width: "48px",
        marginInline: "38px 26px",
        marginTop: "16px",
        height: "fit-content",
        borderRadius: "12px",
        bgcolor: "#ffffff",
        boxShadow: "0px 3px 1px -2px #00000033, 0px 2px 2px 0px #00000024, 0px 1px 5px 0px #0000001F",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 1.2,
        position: "sticky",
        top: 16,
        left: 0,
        zIndex: 1000,
        border: "1px solid #d0a4f1",
        pointerEvents: isTextEditing ? "none" : "auto",
        opacity: isTextEditing ? 0.4 : 1,
      }}
    >
      {selectedComponent && !activeInsertPosition && (
        <>
          {/* Edit Button */}
          <Tooltip title="Edit Content" placement="right">
            <IconButton
            sx={{ padding: "12px !important" }}
              disableRipple
              onClick={(e) => {
                e.stopPropagation();
                if (
                  selectedComponent.type === "widgets" &&
                  typeof onEditWidget === "function"
                ) {
                  onEditWidget(selectedComponent);
                } else if (
                  typeof setIsTextEditing === "function" &&
                  selectedComponent.type === "text"
                ) {
                  setIsTextEditing(selectedComponent.id);
                }
              }}
            >
              <EditIcon color="secondary" />
            </IconButton>
          </Tooltip>
        </>
      )}

      {/* Main buttons - shown when not in insert mode */}
      {!activeInsertPosition && (
        <>
          {/* Add Text */}
          {components.length === 0 && (
            <>
            
              <Tooltip title="Add Text Block" placement="right">
                <IconButton
                  disableRipple
                  onClick={() => createComponent(COMPONENT_TYPES.TEXT)}
                  sx={{ padding: "12px !important" }}
                >
                  <TextFieldsIcon sx={{ color: "#9c27b0" }} />
                </IconButton>
              </Tooltip>

              {/* Add Widget */}
              <Tooltip title="Add Widget" placement="right">
                <IconButton onClick={onOpenWidgetPopup} disableRipple sx={{ padding: "12px !important" }}>
                  <WidgetsIcon sx={{ color: "#9c27b0" }} />
                </IconButton>
              </Tooltip>
            </>
          )}

          {selectedComponent && (
            <>
              {/* Add Above */}
              <Box mt="auto" sx={{ borderTop: "1px solid #0000001F" }}>
              <Tooltip title="Add Above" placement="right">
                <IconButton
                sx={{ padding: "12px !important" }}
                  disableRipple
                  onClick={() => handleInsertClick("above")}
                >
                  <img src={AddUp} alt="Add Above" />
                </IconButton>
              </Tooltip>
              </Box>

              {/* Add Below */}
              <Tooltip title="Add Below" placement="right">
                <IconButton
                sx={{ padding: "12px !important" }}
                  disableRipple
                  onClick={() => handleInsertClick("below")}
                >
                  <img src={AddBelow} alt="Add Below" />
                </IconButton>
              </Tooltip>

              {/* Move Up */}
              <Tooltip title="Move Up" placement="right">
                <IconButton
                sx={{ padding: "12px !important" }}
                  disableRipple
                  onClick={() => onMoveComponent(selectedComponent.id, "up")}
                  disabled={Array.from(components).length === 1}
                >
                  <MoveUpSharpIcon
                    color={
                      Array.from(components).length === 1
                        ? "disabled"
                        : "secondary"
                    }
                  />
                </IconButton>
              </Tooltip>

              {/* Move Down */}
              <Tooltip title="Move Down" placement="right">
                <IconButton
                sx={{ padding: "12px !important" }}
                  disableRipple
                  onClick={() => onMoveComponent(selectedComponent.id, "down")}
                  disabled={Array.from(components).length === 1}
                >
                  <MoveDownSharpIcon
                    color={
                      Array.from(components).length === 1
                        ? "disabled"
                        : "secondary"
                    }
                  />
                </IconButton>
              </Tooltip>

              {/* Delete */}
              <Tooltip title="Delete" placement="right">
                <IconButton
                sx={{ padding: "12px !important" }}
                  disableRipple
                  onClick={() => onDeleteComponent(selectedComponent.id)}
                >
                  <DeleteIcon color="secondary" />
                </IconButton>
              </Tooltip>
            </>
          )}

          {/* Close button moved to the bottom */}
          <Box mt="auto" sx={{ borderTop: "1px solid #0000001F" }}>
            <Tooltip title="Close Sidebar" placement="right">
              <IconButton disableRipple onClick={onCloseSidebar} sx={{ padding: "12px !important" }}>
                <CloseIcon sx={{ color: "#9c27b0" }} />
              </IconButton>
            </Tooltip>
          </Box>
        </>
      )}

      {/* Insert mode buttons - shown when in insert mode */}
      {activeInsertPosition && selectedComponent && (
        <>
          {/* Insert Text */}
          <Tooltip title={`Add Text ${activeInsertPosition}`} placement="right">
            <IconButton
              disableRipple
              onClick={() => handleInsert(COMPONENT_TYPES.TEXT)}
              sx={{ padding: "12px !important" }}
            >
              <TextFieldsIcon color="secondary" />
            </IconButton>
          </Tooltip>

          {/* Insert Widget */}
          <Tooltip
            title={`Add Widget ${activeInsertPosition}`}
            placement="right"
          >
            <IconButton
              disableRipple
              onClick={() => handleInsert(COMPONENT_TYPES.WIDGET)}
              sx={{ padding: "12px !important" }}
            >
              <WidgetsIcon color="secondary" />
            </IconButton>
          </Tooltip>

          {/* Single Cancel button for both modes */}
          <Box mt="auto" sx={{ borderTop: "1px solid #0000001F" }}>
          <Tooltip title="Cancel" placement="right">
            <IconButton disableRipple onClick={handleCancelInsert} sx={{ padding: "12px !important" }}>
              <CloseIcon color="secondary" />
            </IconButton>
          </Tooltip>
          </Box>
        </>
      )}
    </Box>
  );
};

export default Sidebar;
